import React, { useState } from "react";
import { Button, Form, Col, Container, Row, Modal, Table } from "react-bootstrap";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import LoadingSpinner from "./toolbox/LoadingSpinner";
import { getBaseURL } from "./api/api";
import { useAdminSettings } from "./toolbox/provider/AdminProvider";

const EXCEL_EXTENSIONS = [".xlsx"];
const isExcelFile = (name) =>
  name && EXCEL_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

const downloadFile = (url, defaultFilename) => {
  fetch(url)
    .then((res) => res.blob())
    .then((blob) => {
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = defaultFilename;
      a.click();
      window.URL.revokeObjectURL(u);
    })
    .catch(() => toast.error("Download failed."));
};

function AdminFrlUpload() {
  const { adminSettings } = useAdminSettings();
  const adminYear = adminSettings?.adminYear ?? null;

  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState("");

  const fileHandler = (e) => {
    const chosen = e.target.files?.[0];
    if (!chosen) {
      setFile(null);
      return;
    }
    if (!isExcelFile(chosen.name)) {
      toast.error("Please use an Excel file (.xlsx).");
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(chosen);
  };

  const upload = async (override = false) => {
    if (!file) {
      toast.error("Please select a file.");
      return;
    }
    if (!adminYear) {
      toast.error("Admin year is not available. Please try again after logging in.");
      return;
    }
    setIsUploading(true);
    setShowOverrideModal(false);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("adminYear", adminYear);
    if (override) formData.append("override", "true");

    try {
      const res = await axios.post(`${getBaseURL()}/uploadFrl`, formData, {
        headers: { "content-type": "multipart/form-data" },
      });
      const count = res.data?.recordCount;
      const msg =
        count != null
          ? `FRL list imported successfully for ${adminYear}. ${count} record(s) saved.`
          : res.data?.message || `FRL list imported successfully for ${adminYear}.`;
      toast.success(msg);
      setFile(null);
      const fileInput = document.getElementById("frlFilePath");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 409 && data?.code === "FRL_EXISTS") {
        setOverrideMessage(data?.message || "Data for this admin year already exists. Override?");
        setShowOverrideModal(true);
      } else {
        const message =
          data?.message || err.message || "Upload failed. Please try again.";
        toast.error(message);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    upload(false);
  };

  const handleOverrideConfirm = () => {
    upload(true);
  };

  return (
    <Container style={{ marginTop: "110px" }}>
      <ToastContainer />
      {adminYear != null && (
        <p className="text-muted mb-3">Admin year: {adminYear}</p>
      )}
      <Form onSubmit={handleSubmit} encType="multipart/form-data">
        <Form.Row>
          <Form.Group as={Col} controlId="uploadFrlFile">
            <Form.File
              className="col-md-6"
              id="frlFilePath"
              label={file?.name || "Choose Excel file (.xlsx)"}
              onChange={fileHandler}
              accept=".xlsx"
              custom
            />
          </Form.Group>
        </Form.Row>
        <Row className="mt-3">
          <Col>
            <Button
              type="submit"
              variant="primary"
              className="mr-3"
              disabled={isUploading || !file || !adminYear}
            >
              Upload
            </Button>
          </Col>
        </Row>
      </Form>

      {isUploading && (
        <div className="mt-4">
          <LoadingSpinner />
          <p className="text-muted text-center">
            Please wait, your file is being processed.
          </p>
        </div>
      )}

      <Modal
        show={showOverrideModal}
        onHide={() => setShowOverrideModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Override data?</Modal.Title>
        </Modal.Header>
        <Modal.Body>{overrideMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOverrideModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleOverrideConfirm}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      <br />
      <br />
      <strong>SAMPLE EXCEL FILE FORMAT</strong>
      <Table id="adminFrlSample" striped bordered hover className="mt-2">
        <thead>
          <tr>
            <th>first name</th>
            <th>last name</th>
            <th>DOB</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Michael</td>
            <td>Knight</td>
            <td>02/28/2011</td>
          </tr>
        </tbody>
      </Table>
      <Row className="mt-2">
        <Col>
          <Button
            variant="primary"
            className="m-1"
            disabled={!adminYear}
            onClick={() =>
              downloadFile(
                `${getBaseURL()}/frl/export?adminYear=${encodeURIComponent(adminYear)}`,
                `frl-${adminYear || "data"}.xlsx`
              )
            }
          >
            Download all data
          </Button>
          <Button
            variant="outline-primary"
            className="m-1"
            onClick={() =>
              downloadFile(`${getBaseURL()}/frl/sample`, "frl-sample.xlsx")
            }
          >
            Download sample
          </Button>
        </Col>
      </Row>
    </Container>
  );
}

export default AdminFrlUpload;
